import React from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import SyncIcon from "@mui/icons-material/Sync";
import { t } from "ttag";
import {
  deviceDisconnectedReason,
  directionSyncDisableReason,
  fetchButtonDisableReason
} from "./deviceSyncConfig";
import { withButtonTooltip } from "./ButtonTooltip";
import useStyles from "./DeviceSync.style";

const smartSyncTooltip = (
  deviceConnected: boolean,
  loadingCompare: boolean,
  suggestedCount: number
) => {
  if (!deviceConnected) {
    return deviceDisconnectedReason();
  }
  if (suggestedCount === 0) {
    return loadingCompare ? t`Waiting for compare` : t`No missing data`;
  }
  return suggestedCount === 1
    ? t`Smart sync 1 missing item`
    : t`Smart sync ${suggestedCount} missing items`;
};

const SyncToolbar = ({
  deviceConnected,
  loadingCompare,
  syncing,
  suggestedCount,
  onFetch,
  onPullAll,
  onPushAll,
  onSmartSync
}: {
  deviceConnected: boolean;
  loadingCompare: boolean;
  syncing: boolean;
  suggestedCount: number;
  onFetch: () => void;
  onPullAll: () => void;
  onPushAll: () => void;
  onSmartSync: () => void;
}) => {
  const classes = useStyles();

  return (
    <Box className={classes.header}>
      <Box className={classes.headerActions}>
        {withButtonTooltip(
          fetchButtonDisableReason(deviceConnected) || undefined,
          <Button
            className={classes.fetchButton}
            variant="outlined"
            startIcon={
              loadingCompare ? <CircularProgress size={16} color="inherit" /> : <CloudDownloadIcon />
            }
            disabled={!deviceConnected || loadingCompare || syncing}
            onClick={onFetch}
          >
            {t`Fetch`}
          </Button>
        )}
        {withButtonTooltip(
          directionSyncDisableReason(deviceConnected)
            || t`Pull every entity from the device into the cloud`,
          <Button
            className={classes.fetchButton}
            variant="outlined"
            startIcon={<ArrowDownward />}
            disabled={!deviceConnected || syncing}
            onClick={onPullAll}
          >
            {t`Pull all from device`}
          </Button>
        )}
        {withButtonTooltip(
          directionSyncDisableReason(deviceConnected)
            || t`Push every entity from the cloud onto the device`,
          <Button
            className={classes.fetchButton}
            variant="outlined"
            startIcon={<ArrowUpward />}
            disabled={!deviceConnected || syncing}
            onClick={onPushAll}
          >
            {t`Push all to device`}
          </Button>
        )}
        {withButtonTooltip(
          smartSyncTooltip(deviceConnected, loadingCompare, suggestedCount),
          <Button
            className={classes.syncButton}
            variant="contained"
            startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
            disabled={!deviceConnected || loadingCompare || syncing || suggestedCount === 0}
            onClick={onSmartSync}
          >
            {t`Smart sync`}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default SyncToolbar;
